import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { SpaceErrorCode, SpaceNodeKind } from 'shared';

import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import {
  FILE_INVALIDATION_KEYS,
  FOLDER_INVALIDATION_KEYS,
  NODE_ROUTE_SEGMENT,
  SPACE_BACKEND_ROUTE,
} from '../constants/spaces-query.const';
import { SidebarTreeStore } from '../store/sidebar-tree.store';
import { TreeSpaceRef } from '../types/data-room.types';
import { invalidateSpaceFolderLevels } from './use-space-navigation-folders.hook';

type RenameSpaceNodeVariables = {
  id: string;
  kind: SpaceNodeKind;
  name: string;
  space: TreeSpaceRef;
};

type Props = {
  onDuplicateName?: () => void;
  onSuccess?: () => void;
};

/**
 * A folder or a file of a workspace or a data room under a new name — one hook for both, as the
 * server has one rename for both. A renamed folder also leaves the sidebar's levels stale, so they
 * are refreshed and the new name shows there without a reload; a file only touches the listings.
 */
export const useRenameSpaceNode = ({ onDuplicateName, onSuccess }: Props = {}) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();
  const { invalidateQuery: invalidateAfterFile } = useInvalidateQueries(...FILE_INVALIDATION_KEYS);
  const { invalidateQuery: invalidateAfterFolder } = useInvalidateQueries(
    ...FOLDER_INVALIDATION_KEYS,
  );

  const { isPending, mutate: renameNode } = useAppMutation<
    unknown,
    HttpException,
    RenameSpaceNodeVariables
  >([QueryKey.RENAME_SPACE_NODE, companyId], {
    // Replaced: a taken name goes to the field, anything else gets a translated toast.
    defaultErrorHandling: false,
    mutationFn: ({ id, kind, name, space }) =>
      ApiService.patch<unknown, { name: string }>(
        {
          endpoint: SPACE_BACKEND_ROUTE[space.kind],
          routePath: [space.id, NODE_ROUTE_SEGMENT[kind], id],
        },
        { body: { name } },
      ),
    onError: (error) => {
      if (
        error.body?.code === SpaceErrorCode.FILE_NAME_TAKEN ||
        error.body?.code === SpaceErrorCode.FOLDER_NAME_TAKEN
      ) {
        onDuplicateName?.();

        return;
      }

      toast.error(messagesLocale['FAILED_TO_SAVE_CHANGES']);
    },
    onSuccess: (_, { id, kind, name, space }) => {
      if (kind === SpaceNodeKind.FILE) {
        toast.success(messagesLocale['FILE_RENAMED_SUCCESS']);
        invalidateAfterFile();
      } else {
        toast.success(messagesLocale['FOLDER_RENAMED_SUCCESS']);
        invalidateAfterFolder();
        invalidateSpaceFolderLevels(queryClient, companyId, space);
        SidebarTreeStore.getState().renameCrumb(id, name);
      }

      onSuccess?.();
    },
  });

  return { isPending, renameNode };
};
